// ESTADO REAL DE UNA TANDA DE ACCIONES DEL CHAT, Y SU RASTRO EN EL LOG.
//
// Hasta ahora `chat_messages.actions_applied` era un booleano que mezclaba cuatro
// cosas distintas e indistinguibles: aplicado de verdad, rechazado por el usuario
// (rechazar llama a la misma función con la lista vacía), caducado en masa porque
// el presupuesto se reestimó, y aplicado a medias. Con eso no se puede responder a
// "de todas las veces que el agente dijo que aplicaba algo, ¿cuántas se aplicaron?".
// Aquí se separan los estados y se decide, en un solo sitio, si el mensaje queda o
// no marcado como aplicado (que es lo que hace desaparecer el botón Aplicar).
//
// ESPEJO: este fichero vive igual en supabase/functions/_shared/chat-apply-estado.ts
// (el chat corre en el navegador y en el edge). Los ata scripts/check-mirrors-sync.ts.
// Tests: scripts/check-chat-apply-estado.ts

export type EstadoAcciones =
  | "aplicado"    // todas las acciones escribieron
  | "parcial"     // unas sí y otras no
  | "fallido"     // ninguna escribió y hubo errores → el botón Aplicar SIGUE ahí
  | "rechazado"   // el usuario dijo que no (lista vacía)
  | "caducado"    // las líneas ya no existen (reestimación posterior)
  | "pendiente";  // todavía sin decidir

export interface ResultadoAcciones {
  total: number;
  aplicadas: number;
  fallidas: number;
  caducadas?: number;
  rechazadas?: number;
  omitidas?: number;
}

/**
 * Traduce el recuento real a (estado, actions_applied).
 *
 * La regla dura: `actions_applied` NO se pone a true si no se aplicó nada y hubo
 * errores. Antes se marcaba siempre, así que el registro decía "aplicado" de tandas
 * en las que fallaron las 16 acciones, y el botón desaparecía sin haber escrito nada.
 */
export function estadoDeAplicacion(r: ResultadoAcciones): { estado: EstadoAcciones; actionsApplied: boolean } {
  const caducadas = r.caducadas ?? 0;
  const rechazadas = r.rechazadas ?? 0;
  const omitidas = r.omitidas ?? 0;

  // Lista vacía = el usuario ha rechazado la propuesta. No es un fallo y el botón
  // tiene que irse, pero tampoco es un cambio aplicado.
  if (r.total === 0) return { estado: "rechazado", actionsApplied: true };

  if (r.aplicadas >= r.total) return { estado: "aplicado", actionsApplied: true };
  if (r.aplicadas > 0) return { estado: "parcial", actionsApplied: true };

  // Nada aplicado. Si el único motivo es que las líneas ya no existen, la propuesta
  // es irrecuperable y el botón sobra; si hubo errores, se queda para reintentar.
  const soloCaducadas = caducadas > 0 && r.fallidas === 0 && rechazadas === 0 && omitidas === 0;
  if (soloCaducadas) return { estado: "caducado", actionsApplied: true };

  return { estado: "fallido", actionsApplied: false };
}

/** Frase para el usuario. Dice lo que ha pasado de verdad, no una fórmula genérica. */
export function resumenDeAplicacion(r: ResultadoAcciones): string {
  const { estado } = estadoDeAplicacion(r);
  const n = (x: number, s: string, p: string) => `${x} ${x === 1 ? s : p}`;
  switch (estado) {
    case "rechazado":
      return "Propuesta descartada. No se ha tocado nada.";
    case "aplicado":
      return `${n(r.aplicadas, "cambio aplicado", "cambios aplicados")}.`;
    case "parcial":
      return `${r.aplicadas} de ${r.total} cambios aplicados. ${n(r.fallidas + (r.rechazadas ?? 0) + (r.caducadas ?? 0) + (r.omitidas ?? 0), "no se pudo aplicar", "no se pudieron aplicar")}.`;
    case "caducado":
      return "Ningún cambio aplicado: esas líneas ya no existen porque el presupuesto se reestimó después de proponerlas.";
    default:
      return `No se ha aplicado ningún cambio: fallaron ${n(r.total, "acción", "acciones")}.`;
  }
}

// ── Rastro en budget_action_log ──────────────────────────────────────────────

export type ViaDelChat = "chat_boton" | "chat_confirmacion" | "chat_auto";

/** Sub-acción del stack de deshacer: guarda lo necesario para REVERTIR, no para repetir. */
export type SubAccionLog =
  | { type: "insert"; data: { id: string } }
  | { type: "update"; data: { id: string; before: Record<string, unknown> } }
  | { type: "delete"; data: { line: Record<string, unknown> } };

export interface EntradaLogChat {
  action_type: "bulk";
  source: ViaDelChat;
  description: string;
  payload: { subs: SubAccionLog[]; origen: { via: ViaDelChat; message_id: string | null } };
}

const ETIQUETA_VIA: Record<ViaDelChat, string> = {
  chat_boton: "botón Aplicar del chat",
  chat_confirmacion: "confirmación escrita en el chat",
  chat_auto: "aplicado solo por el chat",
};

/**
 * Construye la entrada de budget_action_log de una tanda del chat, o null si no
 * hubo NINGUNA escritura en budget_lines (no se ensucia el stack de deshacer, que
 * solo guarda 20 acciones por proyecto, con tandas que no tocaron el presupuesto).
 *
 * Va como una sola entrada `bulk` y no como N: deshacer lo que aplicó el agente
 * tiene que ser un gesto, igual que aplicarlo lo fue.
 */
export function construirEntradaLogChat(params: {
  via: ViaDelChat;
  messageId: string | null;
  subs: SubAccionLog[];
}): EntradaLogChat | null {
  const subs = (params.subs || []).filter(Boolean);
  if (subs.length === 0) return null;
  const n = subs.length;
  return {
    action_type: "bulk",
    source: params.via,
    description: `${n} línea${n === 1 ? "" : "s"} de presupuesto desde el chat (${ETIQUETA_VIA[params.via]})`,
    payload: { subs, origen: { via: params.via, message_id: params.messageId ?? null } },
  };
}
